import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandHoldingHeart,
  faWandMagicSparkles,
  faCheck,
  faExclamationTriangle,
  faBell,
  faSpinner,
  faMoon,
  faDumbbell,
  faUsers,
  faSpa,
  faUserDoctor,
  faCircleCheck,
  faChevronDown,
  faChevronUp,
  faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import mentalWellnessService, { SelfCareRecommendation } from '../../../services/mentalWellnessService';
import BackButton from '../../../components/BackButton/BackButton';
import './SelfCareActionPlan.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const CATEGORY_CONFIG: Record<string, { icon: IconDefinition; color: string; lightBg: string }> = {
  sleep: { icon: faMoon, color: '#6366f1', lightBg: '#eef2ff' },
  exercise: { icon: faDumbbell, color: '#10b981', lightBg: '#ecfdf5' },
  social: { icon: faUsers, color: '#f59e0b', lightBg: '#fffbeb' },
  mindfulness: { icon: faSpa, color: '#7c3aed', lightBg: '#f5f3ff' },
  professional: { icon: faUserDoctor, color: '#ef4444', lightBg: '#fef2f2' },
};

const getCategoryConfig = (category: string) => {
  return CATEGORY_CONFIG[category.toLowerCase()] || { icon: faHandHoldingHeart, color: '#06b6d4', lightBg: '#ecfeff' };
};

const PRIORITY_COLORS: Record<string, string> = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#ef4444',
};

const formatDate = (iso: string): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ================================================================== */
/*  Toast Component                                                    */
/* ================================================================== */

const Toast: React.FC<{ message: string; type: string; onClose: () => void }> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = { success: faCheck, error: faExclamationTriangle, info: faBell };

  return (
    <div className={`mw-ap-toast mw-ap-toast-${type}`}>
      <span className="mw-ap-toast-icon"><FontAwesomeIcon icon={iconMap[type] || faBell} /></span>
      <span className="mw-ap-toast-message">{message}</span>
      <button className="mw-ap-toast-close" onClick={onClose}>{'\u00D7'}</button>
    </div>
  );
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const SelfCareActionPlan: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { activeProfile } = useProfile();

  const [recommendations, setRecommendations] = useState<SelfCareRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [completingId, setCompletingId] = useState<number | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mentalWellnessService.getRecommendations();
      setRecommendations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
      showToast('Failed to load recommendations.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  const activeRecs = recommendations.filter((r) => !r.is_completed);
  const completedRecs = recommendations.filter((r) => r.is_completed);

  // Group active by category
  const groupedActive: Record<string, SelfCareRecommendation[]> = {};
  activeRecs.forEach((rec) => {
    const cat = rec.category || 'other';
    if (!groupedActive[cat]) groupedActive[cat] = [];
    groupedActive[cat].push(rec);
  });

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const result = await mentalWellnessService.generateRecommendations();
      const toastType: ToastState['type'] = result.new_count > 0 ? 'success' : 'info';
      showToast(result.message, toastType);

      // Use the recommendations from the response if present; fall back to refetch
      if (result.recommendations && result.recommendations.length > 0) {
        setRecommendations((prev) => {
          const completed = prev.filter((r) => r.is_completed);
          return [...result.recommendations, ...completed];
        });
      } else {
        await fetchData();
      }
    } catch (err) {
      console.error('Failed to generate recommendations:', err);
      showToast('Failed to generate recommendations. Please try again.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleComplete = async (id: number) => {
    try {
      setCompletingId(id);
      await mentalWellnessService.completeRecommendation(id);
      showToast('Recommendation completed!', 'success');
      setRecommendations((prev) =>
        prev.map((r) => (r.id === id ? { ...r, is_completed: true, completed_at: new Date().toISOString() } : r))
      );
    } catch (err) {
      console.error('Failed to complete recommendation:', err);
      showToast('Failed to mark as complete.', 'error');
    } finally {
      setCompletingId(null);
    }
  };

  /* ================================================================ */
  /*  Render: Recommendation Card                                      */
  /* ================================================================ */

  const renderRecCard = (rec: SelfCareRecommendation, isCompleted: boolean = false) => {
    const config = getCategoryConfig(rec.category);
    const priorityColor = PRIORITY_COLORS[rec.priority] || '#94a3b8';

    return (
      <div
        className={`mw-ap-rec-card ${isCompleted ? 'completed' : ''}`}
        key={rec.id}
        style={{ borderLeft: `4px solid ${config.color}` }}
      >
        <div className="mw-ap-rec-header">
          <div className="mw-ap-rec-icon" style={{ background: config.lightBg, color: config.color }}>
            <FontAwesomeIcon icon={config.icon} />
          </div>
          <div className="mw-ap-rec-header-text">
            <h4 className={isCompleted ? 'mw-ap-completed-title' : ''}>{rec.title}</h4>
            <div className="mw-ap-rec-meta">
              <span className="mw-ap-rec-category">{rec.category_display || rec.category}</span>
              <span
                className="mw-ap-rec-priority"
                style={{ background: `${priorityColor}15`, color: priorityColor, borderColor: `${priorityColor}30` }}
              >
                {rec.priority} priority
              </span>
            </div>
          </div>
        </div>

        <p className="mw-ap-rec-desc">{rec.description}</p>

        {rec.action_steps && rec.action_steps.length > 0 && (
          <div className="mw-ap-action-steps">
            <span className="mw-ap-steps-label">
              <FontAwesomeIcon icon={faListCheck} /> Action Steps
            </span>
            <ul className="mw-ap-steps-list">
              {rec.action_steps.map((step, i) => (
                <li key={i} className={isCompleted ? 'completed' : ''}>
                  <FontAwesomeIcon icon={isCompleted ? faCircleCheck : faCheck} />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isCompleted && rec.completed_at && (
          <div className="mw-ap-completed-date">
            Completed on {formatDate(rec.completed_at)}
          </div>
        )}

        {!isCompleted && (
          <div className="mw-ap-rec-actions">
            <button
              className="mw-ap-complete-btn"
              style={{ background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)` }}
              onClick={() => handleComplete(rec.id)}
              disabled={completingId === rec.id}
            >
              {completingId === rec.id ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Completing...</>
              ) : (
                <><FontAwesomeIcon icon={faCircleCheck} /> Mark Complete</>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="mw-ap-page">
      {toast && (
        <div className="mw-ap-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Hero */}
      <section className="mw-ap-hero">
        <div className="mw-ap-hero-circle mw-ap-hero-circle-1" />
        <div className="mw-ap-hero-circle mw-ap-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="mw-ap-hero-content">
            <h1 className="mw-ap-hero-title">Self-Care Action Plan</h1>
            <p className="mw-ap-hero-subtitle">
              Personalized recommendations based on your mood patterns and screening results. Small steps lead to big changes.
            </p>
          </div>
        </Container>
      </section>

      <Container>
        <div className="mw-ap-content">
          {/* Generate Button */}
          <div className="mw-ap-generate-row">
            <button
              className="mw-ap-generate-btn"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Generating Recommendations...</>
              ) : (
                <><FontAwesomeIcon icon={faWandMagicSparkles} /> Generate AI Recommendations</>
              )}
            </button>
            <div className="mw-ap-stats-badges">
              <span className="mw-ap-stats-badge active-badge">{activeRecs.length} active</span>
              <span className="mw-ap-stats-badge completed-badge">{completedRecs.length} completed</span>
            </div>
          </div>

          {loading ? (
            <div className="mw-ap-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Loading your recommendations...</p>
            </div>
          ) : activeRecs.length === 0 && completedRecs.length === 0 ? (
            <div className="mw-ap-empty-state">
              <div className="mw-ap-empty-icon">
                <FontAwesomeIcon icon={faHandHoldingHeart} />
              </div>
              <h3>No recommendations yet</h3>
              <p>Click "Generate AI Recommendations" to get personalized self-care suggestions based on your mood and screening data.</p>
            </div>
          ) : (
            <>
              {/* Active Recommendations (grouped by category) */}
              {Object.entries(groupedActive).map(([category, recs]) => {
                const config = getCategoryConfig(category);
                return (
                  <div className="mw-ap-category-group" key={category}>
                    <div className="mw-ap-category-header">
                      <div className="mw-ap-category-icon" style={{ background: config.lightBg, color: config.color }}>
                        <FontAwesomeIcon icon={config.icon} />
                      </div>
                      <h3 className="mw-ap-category-title" style={{ color: config.color }}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </h3>
                      <span className="mw-ap-category-count">{recs.length}</span>
                    </div>
                    <div className="mw-ap-recs-list">
                      {recs.map((rec) => renderRecCard(rec))}
                    </div>
                  </div>
                );
              })}

              {/* Completed Section */}
              {completedRecs.length > 0 && (
                <div className="mw-ap-completed-section">
                  <button
                    className="mw-ap-completed-toggle"
                    onClick={() => setShowCompleted(!showCompleted)}
                  >
                    <FontAwesomeIcon icon={faCircleCheck} />
                    <span>Completed ({completedRecs.length})</span>
                    <FontAwesomeIcon icon={showCompleted ? faChevronUp : faChevronDown} />
                  </button>
                  {showCompleted && (
                    <div className="mw-ap-recs-list mw-ap-completed-list">
                      {completedRecs.map((rec) => renderRecCard(rec, true))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default SelfCareActionPlan;
